import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, FileSpreadsheet, FileText, Edit, Trash2, Layers, Image as ImageIcon, History, ShieldCheck, Barcode } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { EvidenciaStatusBadge } from "@/components/EvidenciaStatusBadge";
import { EsiStatusBadge } from "@/components/EsiStatusBadge";
import { useStatusOptions } from "@/hooks/useStatusOptions";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { usePermission } from "@/hooks/usePermission";
import EvidenciasTab from "@/components/EvidenciasTab";
import HistoryTab from "@/components/HistoryTab";
import EvidenciaStatusTab from "@/components/EvidenciaStatusTab";
import SnDamagedTab from "@/components/SnDamagedTab";

function InfoRow({ label, value, mono = false }) {
  return (
    <div className="flex flex-col gap-0.5 p-3 rounded-md bg-muted/30 border border-border">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className={`text-sm ${mono ? "font-mono" : ""} text-foreground`}>{value || "-"}</span>
    </div>
  );
}

export default function OTDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const canEdit = usePermission("ot_edit");
  const canExport = usePermission("ot_export");
  const canDelete = usePermission("ot_delete");
  const canViewHistory = usePermission("history_view");
  const { options: STATUS_OPTIONS } = useStatusOptions();

  const { data: ot, isLoading, refetch } = useQuery({
    queryKey: ["ot", id],
    queryFn: async () => (await api.get(`/ots/${id}`)).data,
  });

  const downloadFile = async (kind) => {
    try {
      const res = await api.get(`/ots/${id}/${kind}`, { responseType: "blob" });
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `OT_${ot.folio}.${kind === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${kind.toUpperCase()} descargado`);
    } catch (e) {
      toast.error("Error al descargar");
    }
  };

  const changeStatus = async (newStatus) => {
    try {
      await api.patch(`/ots/${id}`, { estatus: newStatus });
      toast.success("Estatus actualizado");
      qc.invalidateQueries({ queryKey: ["ot", id] });
      qc.invalidateQueries({ queryKey: ["ots-list"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      refetch();
    } catch (e) {
      toast.error("Error al actualizar estatus");
    }
  };

  const deleteOT = async () => {
    try {
      await api.delete(`/ots/${id}`);
      toast.success("OT eliminada");
      navigate("/ots");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  if (isLoading) return <div className="text-muted-foreground text-sm">Cargando...</div>;
  if (!ot) return <div className="text-muted-foreground text-sm">OT no encontrada</div>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/ots")}><ArrowLeft className="h-4 w-4 mr-1" /> Volver</Button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-semibold tracking-tight font-mono" data-testid="ot-folio-text">{ot.folio}</h1>
              <StatusBadge status={ot.estatus} />
              <EvidenciaStatusBadge status={ot.estatus_evidencia || "Pendiente de subir"} data-testid="ot-detail-evidencia-badge" />
              <EsiStatusBadge status={ot.estatus_esi || "Pendiente revisión"} data-testid="ot-detail-esi-badge" />
            </div>
            <p className="text-xs text-muted-foreground">Creada el {(ot.created_at || "").substring(0, 16).replace("T", " ")} por {ot.created_by || "-"}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={ot.estatus} onValueChange={changeStatus} disabled={!canEdit}>
            <SelectTrigger className="w-40" data-testid="ot-detail-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          {canExport && (
            <>
              <Button variant="secondary" data-testid="ot-download-excel-button" onClick={() => downloadFile("excel")}>
                <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-400" /> Excel
              </Button>
              <Button variant="secondary" data-testid="ot-download-pdf-button" onClick={() => downloadFile("pdf")}>
                <FileText className="h-4 w-4 mr-2 text-rose-400" /> PDF
              </Button>
            </>
          )}
          {canEdit && (
            <Button variant="secondary" onClick={() => navigate(`/ots/${id}/edit`)} data-testid="ot-edit-button">
              <Edit className="h-4 w-4 mr-2" /> Editar
            </Button>
          )}
          {canDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="icon" data-testid="ot-delete-button"><Trash2 className="h-4 w-4" /></Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Eliminar OT {ot.folio}?</AlertDialogTitle>
                  <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={deleteOT} data-testid="ot-delete-confirm-button">Eliminar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="detalle">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="detalle" data-testid="ot-tab-detalle"><Layers className="h-3.5 w-3.5 mr-1.5" /> Detalle</TabsTrigger>
          <TabsTrigger value="evidencias" data-testid="ot-tab-evidencias"><ImageIcon className="h-3.5 w-3.5 mr-1.5" /> Evidencias</TabsTrigger>
          <TabsTrigger value="sn" data-testid="ot-tab-sn"><Barcode className="h-3.5 w-3.5 mr-1.5" /> SN Dañadas</TabsTrigger>
          <TabsTrigger value="estatus-evidencia" data-testid="ot-tab-estatus-evidencia"><ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> Estatus Evidencia / ESI</TabsTrigger>
          {canViewHistory && <TabsTrigger value="historial" data-testid="ot-tab-historial"><History className="h-3.5 w-3.5 mr-1.5" /> Historial</TabsTrigger>}
        </TabsList>

        <TabsContent value="detalle" className="space-y-3 mt-3">
          {/* Info grid */}
          <Card className="bg-card border-border">
            <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-2">
              <InfoRow label="Cliente" value={ot.cliente} />
              <InfoRow label="Solicitante" value={ot.solicitante} />
              <InfoRow label="Puerto Entrada" value={ot.puerto_entrada} />
              <InfoRow label="Centro" value={ot.centro || "-"} />
              <InfoRow label="Tipos de Trabajo" value={(ot.tipos_trabajo || []).join(", ")} />
              <InfoRow label="Fecha Arribo" value={(ot.fecha_arribo || "").slice(0, 10)} mono />
              <InfoRow label="Total PZ" value={ot.total_pz} mono />
              <InfoRow label="SKUs Únicos" value={ot.unique_skus} mono />
              {ot.observaciones && (
                <div className="col-span-2 md:col-span-4 p-3 rounded-md bg-muted/30 border border-border">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Observaciones</div>
                  <div className="text-sm whitespace-pre-wrap">{ot.observaciones}</div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Items */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">Partidas ({(ot.items || []).length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 border-y border-border">
                    <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                      <th className="py-2 px-3">#</th>
                      <th className="py-2 px-3">PO</th>
                      <th className="py-2 px-3">SKU</th>
                      <th className="py-2 px-3">VPN</th>
                      <th className="py-2 px-3">UPC</th>
                      <th className="py-2 px-3">Pedimento</th>
                      <th className="py-2 px-3 text-right">PZ</th>
                      <th className="py-2 px-3 text-right">Faltante</th>
                      <th className="py-2 px-3 text-right">Sobrante</th>
                      <th className="py-2 px-3">Comentarios</th>
                      <th className="py-2 px-3 text-right">Clas. B</th>
                      <th className="py-2 px-3 text-right">Clas. C</th>
                      <th className="py-2 px-3 text-right">Clas. D</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(ot.items || []).map((it, i) => {
                      const falt = parseInt(it.faltante) || 0;
                      const sobr = parseInt(it.sobrante) || 0;
                      const cB = parseInt(it.clasificacion_b) || 0;
                      const cC = parseInt(it.clasificacion_c) || 0;
                      const cD = parseInt(it.clasificacion_d) || 0;
                      const isShort = falt > 0;
                      const isOver = sobr > 0;
                      return (
                        <tr
                          key={i}
                          className={`border-b border-border/60 hover:bg-muted/30 ${isShort ? "bg-destructive/5" : isOver ? "bg-[hsl(38_92%_55%/0.06)]" : ""}`}
                          data-testid={`ot-detail-row-${i}`}
                        >
                          <td className="py-2 px-3 text-muted-foreground font-mono text-xs">{i + 1}</td>
                          <td className="py-2 px-3 font-mono">{it.po || "-"}</td>
                          <td className="py-2 px-3 font-mono">{it.sku || "-"}</td>
                          <td className="py-2 px-3 font-mono">{it.vpn || "-"}</td>
                          <td className="py-2 px-3 font-mono">{it.upc || "-"}</td>
                          <td className="py-2 px-3 font-mono">{it.pedimento || "-"}</td>
                          <td className="py-2 px-3 text-right font-mono font-semibold">{it.pz || 0}</td>
                          <td className={`py-2 px-3 text-right font-mono ${isShort ? "text-destructive font-bold" : "text-muted-foreground"}`}>
                            {isShort ? `⚠ ${falt}` : "0"}
                          </td>
                          <td className={`py-2 px-3 text-right font-mono ${isOver ? "text-[hsl(var(--status-pending))] font-bold" : "text-muted-foreground"}`}>
                            {isOver ? `⚠ ${sobr}` : "0"}
                          </td>
                          <td className={`py-2 px-3 text-xs ${isShort ? "text-destructive" : isOver ? "text-[hsl(var(--status-pending))]" : "text-muted-foreground"}`}>{it.comentarios || "-"}</td>
                          <td className="py-2 px-3 text-right font-mono text-muted-foreground">{cB || "0"}</td>
                          <td className="py-2 px-3 text-right font-mono text-muted-foreground">{cC || "0"}</td>
                          <td className="py-2 px-3 text-right font-mono text-muted-foreground">{cD || "0"}</td>
                        </tr>
                      );
                    })}
                    {(ot.items || []).length === 0 && (
                      <tr><td colSpan={13} className="text-center py-8 text-muted-foreground text-sm">Sin partidas</td></tr>
                    )}
                  </tbody>
                  <tfoot className="bg-card border-t-2 border-primary/40">
                    <tr>
                      <td colSpan={6} className="py-2 px-3 text-right text-xs uppercase tracking-wider text-muted-foreground">Totales</td>
                      <td className="py-2 px-3 text-right font-mono font-semibold text-primary">{ot.total_pz}</td>
                      <td className={`py-2 px-3 text-right font-mono font-semibold ${(ot.total_faltante || 0) > 0 ? "text-destructive" : "text-muted-foreground"}`} data-testid="ot-detail-total-faltante">
                        {(ot.total_faltante || 0) > 0 ? `⚠ ${ot.total_faltante}` : "0"}
                      </td>
                      <td className={`py-2 px-3 text-right font-mono font-semibold ${(ot.total_sobrante || 0) > 0 ? "text-[hsl(var(--status-pending))]" : "text-muted-foreground"}`} data-testid="ot-detail-total-sobrante">
                        {(ot.total_sobrante || 0) > 0 ? `⚠ ${ot.total_sobrante}` : "0"}
                      </td>
                      <td className="py-2 px-3 text-xs text-muted-foreground">SKUs: <span className="font-mono font-semibold text-foreground">{ot.unique_skus}</span></td>
                      <td className="py-2 px-3 text-right font-mono font-semibold text-muted-foreground">{ot.total_clas_b || 0}</td>
                      <td className="py-2 px-3 text-right font-mono font-semibold text-muted-foreground">{ot.total_clas_c || 0}</td>
                      <td className="py-2 px-3 text-right font-mono font-semibold text-muted-foreground">{ot.total_clas_d || 0}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="evidencias" className="mt-3">
          <EvidenciasTab
            otId={id}
            folio={ot.folio}
            items={ot.items || []}
            puertoEntrada={ot.puerto_entrada || ""}
          />
        </TabsContent>

        <TabsContent value="sn" className="mt-3">
          <SnDamagedTab
            otId={id}
            folio={ot.folio}
            items={ot.items || []}
          />
        </TabsContent>

        <TabsContent value="estatus-evidencia" className="mt-3">
          <EvidenciaStatusTab ot={ot} onUpdated={refetch} />
        </TabsContent>

        <TabsContent value="historial" className="mt-3">
          <HistoryTab otId={id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
